import React, { useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import DOMPurify from "dompurify";
import { SimulationMenuBar } from "../components/menu";
import { SimulationMetricsCarousel } from "../edit/components/callSettings/metrics";
import { SimulationNotFound } from "src/pages/coaching/components/notFound";
import { CourseLearners } from "../../courses/edit/components/learners";
import { BannerView } from "src/components/banner";
import Thumbnail from "src/components/thumbnail";
import Text from "src/components/text";

import { fetchPublishedSimulation, invalidatePublishedSimulation } from "src/redux/actions/coaching/simulation";
import { fetchCourseLearners } from "src/redux/actions/coaching/learners";
import { Simulation } from "src/types/coaching/simulation";
import { CALL_TYPES, DIFFICULTY_CHOICES } from "src/constants/simulation";
import { FileService } from "src/utils/file.service";
import userRoleService from "src/utils/userRoles.service";
import {
  getOverviewBackLabel,
  getManageNavigationRootFrom,
  navigateOverviewBack,
  resolveOverviewBackPath,
  COACHING_MANAGE_PATH,
  COACHING_REPORTS_PATH,
} from "src/pages/coaching/utils/manageNavigation";

import { SimulationContext, SimulationContextProps } from "../edit/context";

import _ from "lodash";
import cx from "classnames";
import styles from "./view.module.scss";

const languageOptions = [
  { label: "English", value: "en" },
  { label: "Spanish", value: "es" },
  { label: "German", value: "de" },
  { label: "French", value: "fr" },
  { label: "Marathi", value: "mr" },
];

export const SimulationOverviewPage: React.FC<object> = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const rootFrom = getManageNavigationRootFrom(location.state);
  const params = useParams();
  const id = params.id;

  const simulation: Simulation | undefined = useSelector((state: any) =>
    _.get(state, `coaching.simulations.simulation[${id}].item`),
  );

  const loading = useSelector((state: any) =>
    _.get(state, `coaching.simulations.simulation[${id}].loading`),
  );

  const simulationErrorCode = useSelector((state: any) =>
    _.get(state, `coaching.simulations.simulation[${id}].error.errors[0].code`),
  );

  const pitchConfiguration = simulation?.type === "elevator_pitch";

  const name = useMemo(() => _.get(simulation, "name") || "", [simulation]);
  const description = useMemo(
    () => _.get(simulation, "description") || "",
    [simulation],
  );
  const banner = useMemo(() => _.get(simulation, "banner"), [simulation]);
  const type = useMemo(() => _.get(simulation, "type"), [simulation]);
  const duration = useMemo(() => _.get(simulation, "duration"), [simulation]);
  const timeBound = useMemo(() => _.get(simulation, "timeBound"), [simulation]);
  const difficulty = useMemo(() => _.get(simulation, "difficulty"), [simulation]);
  const language = useMemo(() => _.get(simulation, "language") || "en", [simulation]);
  const skills = useMemo(() => _.get(simulation, "skills", []) || [], [simulation]);
  const publishedBy = useMemo(() => _.get(simulation, "publishedBy"), [simulation]);

  const callType = useMemo(() => {
    const value = _.get(simulation, "type") || "";
    return CALL_TYPES.find((item) => item.id === value)?.title || "";
  }, [simulation]);

  const difficultyLabel = useMemo(() => {
    const value = _.get(simulation, "difficulty") || "";
    return DIFFICULTY_CHOICES.find((item) => item.value === value)?.label || value;
  }, [simulation]);

  const languageLabel = useMemo(() => {
    return (
      languageOptions.find((option) => option.value === language)?.label ||
      "English"
    );
  }, [language]);

  const publishedByName = useMemo(() => {
    const firstName = _.get(publishedBy, "firstName", "");
    const lastName = _.get(publishedBy, "lastName", "");
    return `${firstName} ${lastName}`.trim();
  }, [publishedBy]);

  const resolvedBackPath = useMemo(
    () => resolveOverviewBackPath(rootFrom),
    [rootFrom],
  );

  const backLabel = useMemo(
    () => getOverviewBackLabel(rootFrom),
    [rootFrom],
  );

  const contextState: SimulationContextProps = useMemo(() => ({ id }), [id]);

  const onBackClick = () => {
    const fallback =
      userRoleService.canAccessCoachingReport() &&
      !userRoleService.canCreateCourse()
        ? COACHING_REPORTS_PATH
        : COACHING_MANAGE_PATH;

    navigateOverviewBack(navigate, location.state, fallback);
  };

  useEffect(() => {
    if (!id) {
      return;
    }

    dispatch(invalidatePublishedSimulation(id));
    dispatch(fetchPublishedSimulation(id));
    dispatch(
      fetchCourseLearners({
        course: id,
        status: "ALL",
        page: 0,
        limit: 10,
      }),
    );
  }, [id,location.pathname]);

  if (simulationErrorCode === 10007) {
    return (
      <SimulationNotFound
        onBack={() => navigateOverviewBack(navigate, location.state, COACHING_MANAGE_PATH)}
      />
    );
  }

  if (!simulation || (loading && !simulation.publishedBy)) {
    return <div className={styles.loading}>Loading simulation...</div>;
  }

  return (
    <SimulationContext.Provider value={contextState}>
      <div className={styles.pageWrapper}>
        <div className={cx(styles.createSimulationContainer, styles.viewMode)}>
          <div className={styles.pageHeader}>
            <div className={styles.backContainer}>
              <div className={styles.backButton} onClick={onBackClick}>
                <i className="paperflite-left-arrow"></i>
                <div>
                  <span className={styles.back}>{backLabel}</span>
                </div>
              </div>
            </div>
            <div className={styles.menubar}>
              <SimulationMenuBar
                simulation={simulation}
                menuState={{ primaryAction: null, secondaryAction: null }}
                onMenuStateChange={() => {}}
                simulationUpdated={false}
                mode="viewer"
              />
            </div>
          </div>

          <div className={styles.overviewLayout}>
            <div className={styles.leftColumn}>
              <div className={styles.bannerHeader}>
                <div
                  className={cx(
                    styles.thumbnail,
                    type === "discovery" && styles.discovery,
                    type === "cold_call" && styles.coldcall,
                    type === "elevator_pitch" && styles.elevatorPitch,
                  )}
                >
                  <BannerView
                    banner={banner as any}
                    defaultBanner="/images/assets/simulationThumbnail.jpg"
                    overlay={false}
                  />
                </div>
                <div className={styles.titleBlock}>
                  <Text className={styles.simulationName} text={name} lines={2} />
                </div>
              </div>

              <div className={styles.metadataPills}>
                {!pitchConfiguration && duration != null && (
                  <div className={styles.metadata}>
                    <i className="paperflite-clock" />
                    {`${duration} Minutes`}
                  </div>
                )}
                {pitchConfiguration && timeBound != null && (
                  <div className={styles.metadata}>
                    <i className="paperflite-clock" />
                    {FileService.formatTimeBound(timeBound, "short")}
                  </div>
                )}
                <div className={styles.metadata}>
                  <i className="paperflite-calendar" />
                  {callType}
                </div>
                {!pitchConfiguration && (
                  <>
                    <div className={styles.metadata}>
                      <i className="paperflite-speedomenter" />
                      {difficultyLabel}
                    </div>
                    <div className={styles.metadata}>
                      <i className="paperflite-language" />
                      {languageLabel}
                    </div>
                  </>
                )}
                {publishedBy && (
                  <div className={cx(styles.metadata, styles.profile)}>
                    <Thumbnail
                      media={publishedBy.icon}
                      defaultImage="/images/assets/default_asset.jpg"
                      className={styles.profileIcon}
                    />
                    by {publishedByName}
                  </div>
                )}
              </div>

              {!pitchConfiguration && (
                <>
                  <div className={styles.descriptionBlock}>
                    <p className={styles.descriptionTitle}>Description</p>
                    <div
                      className={styles.descriptionContent}
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(description),
                      }}
                    />
                  </div>
                  <div className={styles.scenarioCards}>
                    <div className={styles.organizationCard}>
                      <div className={styles.cardHeader}>
                        <div className={styles.cardIconWrap}>
                          <i className="paperflite-buildings" />
                        </div>
                        <div className={styles.cardName}>
                          {simulation?.organization?.name || "no name"}
                        </div>
                      </div>
                      <div className={styles.cardText}>
                        {simulation?.organization?.description || ""}
                      </div>
                    </div>
                    <div className={styles.personaCard}>
                      <div className={styles.cardHeader}>
                        {simulation?.persona?.icon ? (
                          <Thumbnail
                            media={simulation.persona.icon}
                            defaultImage="/images/assets/simulationThumbnail.jpg"
                            className={cx(styles.cardIconWrap, styles.personaImage)}
                          />
                        ) : (
                          <i
                            className={cx(
                              styles.cardIconWrap,
                              "paperflite-user-settings",
                            )}
                          />
                        )}
                        <div className={styles.cardName}>
                          {simulation?.persona?.name || "no name"}
                        </div>
                      </div>
                      <div className={styles.cardText}>
                        {simulation?.persona?.description || ""}
                      </div>
                    </div>
                    <div className={styles.objectivesCard}>
                      <div className={styles.cardHeader}>
                        <div className={styles.cardIconWrap}>
                          <i className="paperflite-objectives" />
                        </div>
                        <div className={styles.cardName}>Objectives</div>
                      </div>
                      <div className={styles.cardText}>
                        {simulation?.objectives?.description || ""}
                      </div>
                    </div>
                  </div>
                </>
              )}

              {pitchConfiguration && (
                <div className={styles.scenarioCards}>
                  <div className={styles.objectivesCard}>
                    <div className={styles.cardHeader}>
                      <i
                        className={cx(
                          styles.cardIconWrap,
                          "paperflite-objectives",
                        )}
                      />
                      <div className={styles.cardName}>Pitch Scenario</div>
                    </div>
                    <div className={styles.cardText}>
                      {simulation?.pitchScenario || "no scenario provided"}
                    </div>
                  </div>
                  <div className={styles.personaCard}>
                    <div className={styles.cardHeader}>
                      <i
                        className={cx(
                          styles.cardIconWrap,
                          "paperflite-user-settings",
                        )}
                      />
                      <div className={styles.cardName}>Pitch Audience</div>
                    </div>
                    <div className={styles.cardText}>
                      {simulation?.pitchAudience || "no audience provided"}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className={styles.rightColumn}>
              <SimulationMetricsCarousel simulation={simulation} />

              <div className={styles.skillsSection}>
                <p className={styles.skillsHeading}>
                  skills learners will gain ({skills.length})
                </p>
                <div className={styles.skillsList}>
                  {skills.map((skill: { id?: string; name?: string }) => (
                    <div key={skill.id} className={styles.skillTag}>
                      {skill.name}
                    </div>
                  ))}
                </div>
              </div>

              <div className={styles.learnersSection}>
                <CourseLearners
                  id={simulation.id!}
                  course={simulation}
                  navigationFrom={location.pathname}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </SimulationContext.Provider>
  );
};
